import { useState, useCallback, useRef, useEffect } from 'react';
import { Download, X, ChevronDown, Loader2 } from 'lucide-react';
import type { Image } from '../../types/image';
import {
  getDownloadUrl,
  downloadFiles,
  DOWNLOAD_SIZE_LABELS,
} from '../../lib/download';
import type { DownloadSize } from '../../lib/download';

interface BulkDownloadBarProps {
  selectedImages: Map<string, Image>;
  onClearSelection: () => void;
}

export const BulkDownloadBar: React.FC<BulkDownloadBarProps> = ({
  selectedImages,
  onClearSelection,
}) => {
  const [downloadSize, setDownloadSize] = useState<DownloadSize>('original');
  const [isSizeOpen, setIsSizeOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sizeRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const selectedCount = selectedImages.size;

  // Close size dropdown on outside click
  useEffect(() => {
    if (!isSizeOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (sizeRef.current && !sizeRef.current.contains(e.target as Node)) {
        setIsSizeOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsSizeOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSizeOpen]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const handleDownloadAll = useCallback(async () => {
    if (selectedImages.size === 0 || isDownloading) return;

    setIsDownloading(true);
    setDownloadProgress({ completed: 0, total: selectedImages.size });
    setError(null);

    const controller = new AbortController();
    abortRef.current = controller;

    const files = Array.from(selectedImages.values()).map((image) => ({
      url: getDownloadUrl(image, downloadSize),
      filename: image.filename,
    }));

    try {
      await downloadFiles(
        files,
        (completed, total) => {
          setDownloadProgress({ completed, total });
        },
        controller.signal,
      );
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        // User cancelled or unmounted
        return;
      }
      setError(
        'Some downloads may have been blocked. Check your popup blocker settings.',
      );
    } finally {
      setIsDownloading(false);
      setDownloadProgress(null);
      abortRef.current = null;
    }
  }, [selectedImages, downloadSize, isDownloading]);

  const handleCancelDownload = useCallback(() => {
    abortRef.current?.abort();
    setIsDownloading(false);
    setDownloadProgress(null);
  }, []);

  const sizes: DownloadSize[] = ['original', 'large', 'medium', 'small'];

  return (
    <div
      className="sticky bottom-0 left-0 right-0 z-40 px-2 sm:px-4 pb-2 sm:pb-4 pointer-events-none"
      style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom, 0px))' }}
      role="toolbar"
      aria-label="Bulk actions"
    >
      <div className="mx-auto max-w-2xl pointer-events-auto">
        <div
          className="flex items-center gap-2 sm:gap-3 bg-white rounded-xl shadow-lg border border-gray-200 px-3 sm:px-4 py-2 sm:py-3"
          style={{ touchAction: 'manipulation' }}
        >
          {/* Selection count */}
          <span className="text-sm font-medium text-gray-700 whitespace-nowrap min-w-fit">
            {isDownloading
              ? `${downloadProgress?.completed ?? 0}/${downloadProgress?.total ?? selectedCount}`
              : `${selectedCount} photo${selectedCount !== 1 ? 's' : ''}`}
          </span>

          <div className="flex-1" />

          {/* Download progress indicator */}
          {isDownloading && (
            <div className="flex items-center gap-1.5 text-sm text-blue-600">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              <span className="text-blue-600">
                {downloadProgress?.completed ?? 0}/{downloadProgress?.total ?? selectedCount}
              </span>
            </div>
          )}

          {/* Size selector */}
          <div className="relative" ref={sizeRef}>
            <button
              onClick={() => setIsSizeOpen(!isSizeOpen)}
              disabled={isDownloading}
              className="flex items-center gap-1 px-2.5 sm:px-3 py-2 sm:py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 touch-manipulation"
              aria-haspopup="listbox"
              aria-expanded={isSizeOpen}
              aria-label={`Download size: ${DOWNLOAD_SIZE_LABELS[downloadSize]}`}
            >
              {/* Show short label on mobile, full label on desktop */}
              <span className="sm:hidden">{downloadSize === 'original' ? 'Orig' : DOWNLOAD_SIZE_LABELS[downloadSize].split(' ')[0]}</span>
              <span className="hidden sm:inline">{DOWNLOAD_SIZE_LABELS[downloadSize]}</span>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform shrink-0 ${isSizeOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {isSizeOpen && (
              <div
                className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                role="listbox"
                aria-label="Select download size"
              >
                {sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => {
                      setDownloadSize(size);
                      setIsSizeOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 sm:py-2 text-sm transition-colors touch-manipulation ${
                      size === downloadSize
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    role="option"
                    aria-selected={size === downloadSize}
                  >
                    {DOWNLOAD_SIZE_LABELS[size]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Download / Cancel button */}
          {isDownloading ? (
            <button
              onClick={handleCancelDownload}
              className="flex items-center gap-1.5 px-3 sm:px-4 py-2 sm:py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors touch-manipulation"
              aria-label="Cancel download"
            >
              <X className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Cancel</span>
            </button>
          ) : (
            <button
              onClick={handleDownloadAll}
              className="flex items-center gap-1.5 px-3 sm:px-4 py-2 sm:py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 touch-manipulation"
              aria-label={`Download ${selectedCount} photo${selectedCount !== 1 ? 's' : ''}`}
            >
              <Download className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Download All</span>
            </button>
          )}

          {/* Clear selection */}
          <button
            onClick={onClearSelection}
            disabled={isDownloading}
            className="p-2 sm:p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 touch-manipulation"
            aria-label="Clear selection"
            title="Clear selection"
          >
            <X className="w-5 h-5 sm:w-4 sm:h-4" />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mt-2 text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2 shadow border border-red-200">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};
